import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import HomePage from './pages/HomePage';                  // eager: LCP route
// Secondary routes are code-split to keep the initial bundle small.
// Each one becomes its own JS chunk that downloads only when visited.
const SobreMi = lazy(() => import('./pages/SobreMi'));
const AvisoLegal = lazy(() => import('./pages/AvisoLegal'));
const PoliticaPrivacidad = lazy(() => import('./pages/PoliticaPrivacidad'));
const PoliticaRedes = lazy(() => import('./pages/PoliticaRedes'));
const CertificadoSeguridad = lazy(() => import('./pages/CertificadoSeguridad'));
const PoliticaCookies = lazy(() => import('./pages/PoliticaCookies'));
const SectorPublico = lazy(() => import('./pages/SectorPublico'));
const SectorPrivado = lazy(() => import('./pages/SectorPrivado'));
const SectorDerechoPublico = lazy(() => import('./pages/SectorDerechoPublico'));
const TerminosCondiciones = lazy(() => import('./pages/TerminosCondiciones'));
const EscanerExpress = lazy(() => import('./pages/EscanerExpress'));
const Tarifas = lazy(() => import('./pages/Tarifas'));
const GeneradorLegal = lazy(() => import('./pages/GeneradorLegal'));
const TestLegalInyeccion = lazy(() => import('./pages/TestLegalInyeccion'));
const CasosExito = lazy(() => import('./pages/CasosExito'));
const Recursos = lazy(() => import('./pages/Recursos'));
const ADCATemporal = lazy(() => import('./pages/ADCATemporal'));
const SelfHealingSaaS = lazy(() => import('./pages/SelfHealingSaaS'));
const SecureReceipt = lazy(() => import('./pages/SecureReceipt'));
const CanalDenuncias = lazy(() => import('./pages/CanalDenuncias'));
const PoliticaCalidad = lazy(() => import('./pages/PoliticaCalidad'));
const GuiaFacil = lazy(() => import('./pages/GuiaFacil'));
const SimuladorEslabondebil = lazy(() => import('./pages/SimuladorEslabondebil'));
const PoliticaIA = lazy(() => import('./pages/PoliticaIA'));

import { Toaster } from './components/ui/sonner';
import LoadingSpinner from './components/LoadingSpinner';
import { Helmet } from 'react-helmet-async';
import CookiePreferencesButton from './components/CookiePreferencesButton';
import ChatWidget from './components/ChatWidget';
import ComplianceWidget from './components/ComplianceWidget';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import SecurityMonitorWidget from './components/SecurityMonitorWidget';
import AISentinelWidget from './components/AISentinelWidget';

import { initCookieConsent } from './lib/cookieConsent';

// RouteFallback removed – using LoadingSpinner instead.

function HashScrollHandler() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace('#', '');
      let attempts = 0;
      const maxAttempts = 30; // 30 * 50ms = 1500ms
      
      const tryScroll = () => {
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
          return true;
        }
        return false;
      };

      // Intentar de inmediato
      if (tryScroll()) return;

      // Si no está listo, intentar periódicamente con un intervalo (para componentes lazy)
      const interval = setInterval(() => {
        attempts++;
        const success = tryScroll();
        if (success || attempts >= maxAttempts) {
          clearInterval(interval);
        }
      }, 50);

      return () => clearInterval(interval);
    }
  }, [location]);

  return null;
}

function App() {
  useEffect(() => {
    initCookieConsent();
    document.body.classList.add('react-loaded');
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .register('/sw.js')
        .then((reg) => {
          // Detect new SW and prompt update; force takeover when ready
          if (reg.waiting) reg.waiting.postMessage({ type: 'SKIP_WAITING' });
          reg.addEventListener('updatefound', () => {
            const nw = reg.installing;
            if (!nw) return;
            nw.addEventListener('statechange', () => {
              if (nw.state === 'installed' && navigator.serviceWorker.controller) {
                // A newer SW is installed and an old one was in control — reload to pick it up
                window.location.reload();
              }
            });
          });
        })
        .catch((err) => console.warn('[SW] registration failed', err));

      // When the controlling SW changes, hard-reload once to clear stale chunks
      let reloaded = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (reloaded) return;
        reloaded = true;
        window.location.reload();
      });
    }
  }, []);

  return (
      <Router>
        <HashScrollHandler />
    <Helmet>
      <title>Auditorialegal – Servicios Legales</title>
      <meta name="description" content="Auditoría forense de ciberseguridad y cumplimiento normativo" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
    </Helmet>
    <Suspense fallback={<LoadingSpinner />}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/sobre-mi" element={<SobreMi />} />
          <Route path="/aviso-legal" element={<AvisoLegal />} />
          <Route path="/politica-privacidad" element={<PoliticaPrivacidad />} />
          <Route path="/politica-privacidad-redes" element={<PoliticaRedes />} />
          <Route path="/certificado-seguridad" element={<CertificadoSeguridad />} />
          <Route path="/politica-cookies" element={<PoliticaCookies />} />
          <Route path="/sector-publico" element={<SectorPublico />} />
          <Route path="/sector-privado" element={<SectorPrivado />} />
          <Route path="/sector-derecho-publico" element={<SectorDerechoPublico />} />
          <Route path="/terminos-condiciones" element={<TerminosCondiciones />} />
          <Route path="/escaner-express" element={<EscanerExpress />} />
          <Route path="/tarifas" element={<Tarifas />} />
          <Route path="/generador-legal" element={<GeneradorLegal />} />
          <Route path="/test-legal" element={<TestLegalInyeccion />} />
          <Route path="/casos-exito" element={<CasosExito />} />
          <Route path="/productos" element={<Recursos />} />
          <Route path="/recursos" element={<Navigate to="/productos" replace />} />
          <Route path="/adca-temporal" element={<ADCATemporal />} />
          <Route path="/self-healing" element={<SelfHealingSaaS />} />
          <Route path="/secure-receipt" element={<SecureReceipt />} />
          <Route path="/secure-expenses" element={<SecureReceipt />} />
          <Route path="/canal-denuncias" element={<CanalDenuncias />} />
          <Route path="/politica-calidad" element={<PoliticaCalidad />} />
          <Route path="/guia-facil" element={<GuiaFacil />} />
          <Route path="/simulador-eslabon-debil" element={<SimuladorEslabondebil />} />
          <Route path="/politica-ia" element={<PoliticaIA />} />
        </Routes>
      </Suspense>
      <CookiePreferencesButton />
      <ChatWidget />
      <ComplianceWidget />
      <PWAInstallPrompt />
      <SecurityMonitorWidget />
      <AISentinelWidget />
      <Toaster />

    </Router>
  );
}

export default App;
